import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Brand, ICustomer, userIdType } from '@signal/asp-data-commons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SubscriptionList } from 'src/app/modules/shared/models/asp.types';
import { DeliveryChecklistService } from 'src/app/modules/shared/services/delivery-checklist.service';
import { unsubscribeSubscriptions } from 'src/app/modules/shared/services/util.service';
import { DeliveryChecklistState, USER } from 'src/app/modules/store/delivery-checklist/delivery-checklist.reducer';
import { selectCustomerDetails, selectCustomerSignatureObject, selectTechnicianSignatureObject } from 'src/app/modules/store/delivery-checklist/delivery-checklist.selectors';
import { SignaturePadComponent } from '../signature-pad/signature-pad.component';
import { userProfile } from '../../../shared/services/util.service';
import { environment } from 'src/environments/environment.dev';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-signatures',
  templateUrl: './signatures.component.html',
  styleUrls: ['./signatures.component.scss']
})
export class SignaturesComponent implements OnInit {
  @Input() enableSig: boolean;
  @Input() isUpdate: boolean;
  @Output() formEdited: EventEmitter<boolean> = new EventEmitter();

  private readonly unsubscribe$ = new Subject<void>();
  private readonly subs: SubscriptionList = {};
  customer: ICustomer;
  isShowTechSpinner = false;
  USER = USER;
  customerSignatureObject: any = {
    id: '',
    firstName: '',
    lastName: '',
    date: '',
    data: '',
    type: this.USER.CUSTOMER
  }
  technicianSignatureObject: any = {
    id: '',
    firstName: '',
    lastName: '',
    date: '',
    data: '',
    type: this.USER.TECHNICIAN,
    isSignatureExists: false
  }
  isLexus = environment.brand === Brand.LEXUS ;
  userProfile: any = userProfile;

  constructor(
    private readonly dialogRef: MatDialog,
    private readonly deliveryChecklistStore: Store<DeliveryChecklistState>,
    private readonly digiCheckService: DeliveryChecklistService
  ) { }

  ngOnInit(): void {
    this.subs.customer = this.deliveryChecklistStore.select(selectCustomerDetails).subscribe(data => {
      this.customer = data;
      if (data.firstName !== this.customerSignatureObject.firstName || data.lastName !== this.customerSignatureObject.lastName) {
        this.customerSignatureObject = {
          ...this.customerSignatureObject,
          firstName: data.firstName,
          lastName: data.lastName
        }
      }
    })

    this.subs.customerObj = this.deliveryChecklistStore.select(selectCustomerSignatureObject).subscribe(data => {
      if (this.isUpdate && data?.customerSignId && !this.customerSignatureObject.data) {
        this.customerSignatureObject = {
          ...this.customerSignatureObject,
          firstName: data.firstName,
          lastName : data.lastName
        }
        this.getSignature(data.customerSignId, this.USER.CUSTOMER, userIdType.USERID);
      }
    })

    this.subs.consultantObj = this.deliveryChecklistStore.select(selectTechnicianSignatureObject).subscribe(data => {
      if (this.isUpdate && data?.salesAdvisorSignId && !this.technicianSignatureObject.data) {
        this.technicianSignatureObject = {
          ...this.technicianSignatureObject,
          firstName : data.firstName,
          lastName : data.lastName,
          ownerId : data?.ownerId ? data.ownerId : ''
        }
        this.getSignature(data.salesAdvisorSignId, this.USER.TECHNICIAN, userIdType.USERID);
      }
    })
  }
  getSignatureForTech(id, UserTypeId, getType?: string) {
    this.digiCheckService.getSignature(id, UserTypeId).subscribe((res: any) => {
      if (res) {
        if (res?.userId) {
          this.technicianSignatureObject = {
            ...this.technicianSignatureObject,
            id : res.userId,
            isSignatureExists : true,
            data : res.data,
            date : DateTime.fromISO(res.metaData.createdAt).toFormat('MM/dd/yyyy')
          }
          if (getType === 'ADD') {
            this.openSignDialogForConsultant();
          }
        }
      }
    }, _error => {
      if (getType === 'ADD') {
        this.openSignDialogForConsultant();
      }
    });
  }

  getSignatureForCustomer(id, UserTypeId) {
    this.digiCheckService.getSignature(id, UserTypeId).subscribe((res: any) => {
      if (res) {
        if (res?.userId) {
          this.customerSignatureObject = {
            ...this.customerSignatureObject,
            id : res.userId,
            isSignatureExists : true,
            data : res.data,
            date : DateTime.fromISO(res.metaData.createdAt).toFormat('MM/dd/yyyy')
          }
        }
      }
    });
  }

  getSignature(id, type, UserTypeId, getType?: string) {
    if (type === this.USER.TECHNICIAN) {
      this.getSignatureForTech(id, UserTypeId, getType);
    }
    else if (type === this.USER.CUSTOMER) {
      this.getSignatureForCustomer(id, UserTypeId);
    }
  }

  openSignDialogForConsultant() {
    this.isShowTechSpinner = false;
    const ref = this.dialogRef.open(SignaturePadComponent, {
      width: '500px',
      data: { sigData: this.technicianSignatureObject, isLoggedIn: true }
    });
    ref.afterClosed().pipe(takeUntil(this.unsubscribe$)).subscribe((result: any) => {
      this.technicianSignatureObject = {
        id: '',
        firstName: '',
        lastName: '',
        date: '',
        data: '',
        type: this.USER.TECHNICIAN,
        isSignatureExists: false
      }
      if (result?.sigNatureObjct && result?.sigNatureObjct?.data) {
        this.technicianSignatureObject = result.sigNatureObjct;
      }
    });
  }

  addSignature(type) {
    this.formEdited.emit(false);
    if (type === this.USER.TECHNICIAN) {
      this.isShowTechSpinner = true;
      this.technicianSignatureObject = {
        ...this.technicianSignatureObject,
        firstName : this.userProfile?.name?.firstName,
        lastName : this.userProfile?.name?.lastName,
        ownerId : this.userProfile?.userId
      }
      this.getSignature(this.userProfile?.userId, type, userIdType.OWNERID, 'ADD');
    }
    else if (type === this.USER.CUSTOMER) {
      const ref = this.dialogRef.open(SignaturePadComponent, {
        width: '500px',
        data: { sigData: this.customerSignatureObject }
      });
      ref.afterClosed().pipe(takeUntil(this.unsubscribe$)).subscribe((result: any) => {
        this.customerSignatureObject = {
          id: '',
          firstName: this.customer.firstName,
          lastName: this.customer.lastName,
          date: '',
          data: '',
          type: this.USER.CUSTOMER
        };
        if (result?.sigNatureObjct && result?.sigNatureObjct?.data) {
          this.customerSignatureObject = result.sigNatureObjct;
        }
      });
    }
  }

  ngOnDestroy(): void {
    unsubscribeSubscriptions(this.subs);
  }

}
